import React from 'react';
// styled-components must be a NAMED import here — the default import breaks under
// Node ESM in this package (see homeHero.tsx note).
import { styled, createGlobalStyle } from 'styled-components';

/**
 * IntelliAuth marketing design system — self-contained, tenant-specific.
 *
 * Ported from @intelliauth/ui's token theme so the CMS-rendered marketing site
 * matches intelliauth.io. Colours are CSS custom properties with LIGHT defaults
 * plus a `html.ia-dark` override, so the whole IntelliAuth site flips
 * light/dark instantly. The global style is emitted ONLY on pages that render an
 * `ia*` block (createGlobalStyle mounts with the component), so Cymmetri and
 * Bastion pages are completely unaffected.
 *
 * These primitives are used exclusively by the `ia*` blocks — they are the
 * IntelliAuth tenant's own components, isolated from every other tenant.
 */

// ── Theme tokens (light default + .ia-dark override) ───────────────────────
export const IaGlobalStyle = createGlobalStyle`
  :root {
    --ia-bg:#F8FAFC; --ia-card:#FFFFFF; --ia-text:#0E1525; --ia-text2:#4A5568;
    --ia-text3:#8B95A8; --ia-inv:#F8FAFC; --ia-border:#DEE1E6;
    --ia-primary:#023ECA; --ia-primary-h:#012169; --ia-primary-fg:#FFFFFF;
    --ia-cta:#47D7AC; --ia-cta-h:#38B08C; --ia-cta-fg:#06231C;
    --ia-success:#38B08C; --ia-info:#3B82F6;
    --ia-shadow-sm:0 1px 2px 0 rgb(0 0 0 / .06);
    --ia-shadow-md:0 2px 4px -1px rgb(0 0 0 / .08), 0 4px 8px -2px rgb(0 0 0 / .06);
    --ia-shadow-lg:0 4px 8px -2px rgb(0 0 0 / .10), 0 16px 32px -4px rgb(0 0 0 / .12);
    --ia-glow-a:rgba(2,62,202,.15); --ia-glow-b:rgba(56,176,140,.15); --ia-glow-c:rgba(59,130,246,.10);
  }
  html.ia-dark {
    --ia-bg:#0b1220; --ia-card:#111b2e; --ia-text:#F5F7FA; --ia-text2:#B3BCCF;
    --ia-text3:#8B97B0; --ia-inv:#0b1220; --ia-border:rgba(255,255,255,.12);
    --ia-primary:#5B8DEF; --ia-primary-h:#9CBBF7; --ia-primary-fg:#071022;
    --ia-cta:#47D7AC; --ia-cta-h:#79E2C2; --ia-cta-fg:#06231C;
    --ia-success:#4FC3A0; --ia-info:#6B8AE0;
    --ia-shadow-sm:0 1px 2px 0 rgb(0 0 0 / .4);
    --ia-shadow-md:0 4px 10px -2px rgb(0 0 0 / .5);
    --ia-shadow-lg:0 10px 30px -6px rgb(0 0 0 / .6);
    --ia-glow-a:rgba(91,141,239,.18); --ia-glow-b:rgba(79,195,160,.16); --ia-glow-c:rgba(107,138,224,.12);
  }
  body {
    background:var(--ia-bg);
    color:var(--ia-text);
    font-family:'Inter',system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;
    transition:background-color .2s ease, color .2s ease;
  }
`;

/**
 * Anti-FOUC bootstrap: applies `.ia-dark` before first paint from the persisted
 * preference (or OS setting for "system"). Rendered inline by the iaNav block
 * (the first block on the page) so it runs during HTML parse. The full toggle
 * wiring + persistence lives in apps/web entry-client `setupIaTheme`.
 */
export const IA_THEME_BOOTSTRAP =
  "(function(){try{var p=localStorage.getItem('ia-theme')||'system';var d=p==='dark'||(p==='system'&&window.matchMedia&&window.matchMedia('(prefers-color-scheme:dark)').matches);document.documentElement.classList.toggle('ia-dark',d);}catch(e){}})();";

// ── Layout primitives ──────────────────────────────────────────────────────
export const Wrap = styled.div<{ $max?: number }>`
  max-width: ${(p) => p.$max ?? 1216}px;
  margin: 0 auto;
  padding: 0 24px;
`;

export const Section = styled.section<{ $pt?: number; $pb?: number }>`
  padding-top: ${(p) => p.$pt ?? 96}px;
  padding-bottom: ${(p) => p.$pb ?? 96}px;
`;

export const Eyebrow = styled.h2`
  font-size: 30px;
  line-height: 1.15;
  font-weight: 600;
  letter-spacing: -0.4px;
  color: var(--ia-text);
  margin: 0;
  @media (min-width: 640px) { font-size: 36px; }
`;

export const Lede = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: var(--ia-text2);
  margin: 16px auto 0;
  max-width: 640px;
`;

// ── Button (matches the DS Button: mint CTA fill / outline, uppercase) ──────
export const Btn = styled.a<{ $variant?: 'primary' | 'outline'; $size?: 'md' | 'lg'; $block?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  border-radius: 6px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease, border-color .15s ease, box-shadow .15s ease;
  width: ${(p) => (p.$block ? '100%' : 'auto')};
  height: ${(p) => (p.$size === 'lg' ? 48 : 40)}px;
  padding: 0 ${(p) => (p.$size === 'lg' ? 24 : 16)}px;
  font-size: ${(p) => (p.$size === 'lg' ? 14 : 13)}px;
  ${(p) =>
    p.$variant === 'outline'
      ? `background: transparent; color: var(--ia-text); border: 1px solid var(--ia-border);
         &:hover { border-color: var(--ia-success); background: rgba(56,176,140,.06); }`
      : `background: var(--ia-cta); color: var(--ia-cta-fg); border: 1px solid transparent;
         &:hover { background: var(--ia-cta-h); }`}
  & svg { width: ${(p) => (p.$size === 'lg' ? 18 : 16)}px; height: ${(p) => (p.$size === 'lg' ? 18 : 16)}px; }
`;

export const Card = styled.div`
  background: color-mix(in srgb, var(--ia-card) 85%, transparent);
  border: 1px solid var(--ia-border);
  border-radius: 8px;
  box-shadow: var(--ia-shadow-sm);
  backdrop-filter: blur(16px);
`;

// ── Icon set (inline, currentColor, 24×24 stroke) ──────────────────────────
const P = (d: string) => (
  <path d={d} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
);
const FILL = (d: string) => <path d={d} fill="currentColor" />;

const ICONS: Record<string, React.ReactNode> = {
  workflow: <>{P('M3 3h6v6H3z')}{P('M15 15h6v6h-6z')}{P('M6 9v3a3 3 0 0 0 3 3h3')}{P('M15 18h-1')}</>,
  building: <>{P('M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18')}{P('M6 12H4a2 2 0 0 0-2 2v8h4')}{P('M18 9h2a2 2 0 0 1 2 2v11h-4')}{P('M10 6h4M10 10h4M10 14h4M10 18h4')}</>,
  shield: <>{P('M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z')}{P('M9 12l2 2 4-4')}</>,
  code: <>{P('M16 18l6-6-6-6')}{P('M8 6l-6 6 6 6')}</>,
  lock: <>{P('M5 11h14v10H5z')}{P('M8 11V7a4 4 0 0 1 8 0v4')}</>,
  terminal: <>{P('M4 17l6-6-6-6')}{P('M12 19h8')}</>,
  users: <>{P('M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2')}{P('M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z')}{P('M23 21v-2a4 4 0 0 0-3-3.87')}{P('M16 3.13a4 4 0 0 1 0 7.75')}</>,
  globe: <>{P('M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z')}{P('M2 12h20')}{P('M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z')}</>,
  check: P('M20 6L9 17l-5-5'),
  arrowRight: <>{P('M5 12h14')}{P('M12 5l7 7-7 7')}</>,
  sparkles: <>{P('M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z')}{P('M19 4v4M21 6h-4M5 17v3M6.5 18.5h-3')}</>,
  sun: <>{P('M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z')}{P('M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4')}</>,
  moon: P('M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z'),
  monitor: <>{P('M3 4h18v12H3z')}{P('M8 20h8M12 16v4')}</>,
  twitter: FILL('M22 5.9c-.7.3-1.5.5-2.3.6.8-.5 1.5-1.3 1.8-2.3-.8.5-1.7.8-2.6 1a4.1 4.1 0 0 0-7 3.7A11.6 11.6 0 0 1 3.4 4.6a4.1 4.1 0 0 0 1.3 5.5c-.7 0-1.3-.2-1.9-.5a4.1 4.1 0 0 0 3.3 4c-.6.2-1.2.2-1.8.1a4.1 4.1 0 0 0 3.8 2.9A8.3 8.3 0 0 1 2 18.3a11.6 11.6 0 0 0 6.3 1.8c7.5 0 11.6-6.2 11.6-11.6v-.5c.8-.6 1.5-1.3 2.1-2.1z'),
  github: FILL('M12 2a10 10 0 0 0-3.2 19.5c.5.1.7-.2.7-.5v-1.7c-2.8.6-3.4-1.3-3.4-1.3-.5-1.2-1.1-1.5-1.1-1.5-.9-.6.1-.6.1-.6 1 .1 1.5 1 1.5 1 .9 1.5 2.3 1.1 2.9.8.1-.6.3-1.1.6-1.4-2.2-.2-4.6-1.1-4.6-5 0-1.1.4-2 1-2.7-.1-.3-.4-1.3.1-2.6 0 0 .8-.3 2.7 1a9.4 9.4 0 0 1 5 0c1.9-1.3 2.7-1 2.7-1 .5 1.3.2 2.3.1 2.6.6.7 1 1.6 1 2.7 0 3.9-2.4 4.8-4.6 5 .3.3.6.9.6 1.9v2.8c0 .3.2.6.7.5A10 10 0 0 0 12 2z'),
  linkedin: FILL('M4.98 3.5A2.5 2.5 0 1 1 0 3.5a2.5 2.5 0 0 1 4.98 0zM.5 8h4V24h-4zM8 8h3.8v2.2h.1c.5-1 1.8-2.2 3.9-2.2 4.2 0 5 2.7 5 6.3V24h-4v-7c0-1.7 0-3.8-2.3-3.8s-2.7 1.8-2.7 3.7V24H8z'),
};

export function Icon({ name, size = 24 }: { name: string; size?: number }) {
  const node = ICONS[name] ?? ICONS.check;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>
      {node}
    </svg>
  );
}

// ── IntelliAuth logo (gradient shield + wordmark) ──────────────────────────
export function IaLogo({ size = 32 }: { size?: number }) {
  const gid = 'ia-shield-grad';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id={gid} x1="8" y1="4" x2="32" y2="36" gradientUnits="userSpaceOnUse">
            <stop stopColor="#5B8DEF" />
            <stop offset="1" stopColor="#023ECA" />
          </linearGradient>
        </defs>
        <path d="M20 3l13 4.5v9C33 27 20 37 20 37S7 27 7 16.5v-9L20 3z" fill={`url(#${gid})`} />
        <rect x="14.5" y="19" width="11" height="9" rx="2" fill="#EAF1FE" />
        <path d="M16.5 19v-2.5a3.5 3.5 0 0 1 7 0V19" stroke="#C7DBFE" strokeWidth="2" fill="none" />
        <circle cx="20" cy="23.5" r="1.6" fill="#023ECA" />
      </svg>
      <span style={{ fontSize: size === 40 ? 24 : size === 24 ? 16 : 18, fontWeight: 700, letterSpacing: '-0.3px', color: 'var(--ia-text)' }}>
        IntelliAuth
      </span>
    </span>
  );
}

// Grid helper used by several blocks.
export const Grid = styled.div<{ $min?: number; $cols?: number; $gap?: number }>`
  display: grid;
  gap: ${(p) => p.$gap ?? 24}px;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  @media (min-width: 768px) { grid-template-columns: repeat(${(p) => Math.min(p.$cols ?? 3, 2)}, minmax(0, 1fr)); }
  @media (min-width: 1024px) { grid-template-columns: repeat(${(p) => p.$cols ?? 3}, minmax(0, 1fr)); }
`;

export const SectionHead = styled.div`
  text-align: center;
  margin-bottom: 64px;
`;
